<template>
  <div class="voice-room-speakers" :class="layoutClass">
    <!-- Main Host -->
    <div class="speaker main-speaker" v-if="mainHost">
      <div class="speaker-avatar" :class="{ 'active': mainHost.isActive, 'speaking': mainHost.isSpeaking }">
        <img :src="mainHost.avatar" :alt="mainHost.name" />
        <div class="mic-status" :class="{ 'active': !mainHost.isMuted }">
          <component :is="mainHost.isMuted ? 'MicOffIcon' : 'MicIcon'" size="1x" />
        </div>
      </div>
      <div class="speaker-name">{{ mainHost.name }}</div>
    </div>

    <!-- Other Speakers -->
    <div class="speakers-grid" :class="gridClass">
      <div v-for="speaker in speakers.filter(s => mainHost ? s.id !== mainHost.id : true)" 
           :key="speaker.id"
           class="speaker"
           :class="{ 'medium-size': shouldUseMediumSize }">
        <div class="speaker-avatar" :class="{ 'active': speaker.isActive, 'speaking': speaker.isSpeaking }">
          <img :src="speaker.avatar" :alt="speaker.name" />
          <div class="mic-status" :class="{ 'active': !speaker.isMuted }">
            <component :is="speaker.isMuted ? 'MicOffIcon' : 'MicIcon'" size="1x" />
          </div>
          <span class="speaker-level" v-if="speaker.level">{{ speaker.level }}</span>
        </div>
        <div class="speaker-name">{{ speaker.name }}</div>
      </div>
      <!-- Show More Button -->
      <div v-if="hasMoreSpeakers" class="speaker show-more">
        <div class="speaker-avatar">
          <span>+{{ remainingSpeakersCount }}</span>
        </div>
      </div>
      <!-- Request to Speak Button for Audience -->
      <div
        v-if="!isCurrentUserHost"
        class="speaker request-speaker-wrapper"
        :class="{ 'medium-size': shouldUseMediumSize }"
      >
        <b-button
          variant="outline-secondary"
          pill
          class="request-speak-btn"
          @click="requestToSpeak"
        >
          <PlusIcon size="1.5x" />
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BRow, BCol, BAvatar, BButton } from 'bootstrap-vue'
import MicIcon from 'vue-feather-icons/icons/MicIcon'
import MicOffIcon from 'vue-feather-icons/icons/MicOffIcon'
import PlusIcon from 'vue-feather-icons/icons/PlusIcon'
import { mapGetters } from 'vuex'

export default {
  name: 'VoiceRoomSpeakers',

  components: {
    BRow,
    BCol,
    BAvatar,
    BButton,
    MicIcon,
    MicOffIcon,
    PlusIcon,
  },

  props: {
    isCurrentUserHost: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('voiceRoom', [
      'mainHost',
      'speakers',
    ]),

    activeSpeakers() {
      const hostId = this.mainHost ? this.mainHost.id : null;
      const currentSpeakers = Array.isArray(this.speakers) ? this.speakers : [];
      return currentSpeakers.filter(speaker => speaker.id !== hostId);
    },

    displayedSpeakers() {
      const maxDisplay = this.activeSpeakers.length <= 1 ? 1 : 
                        this.activeSpeakers.length <= 3 ? 3 : 12;
      return this.activeSpeakers.slice(0, maxDisplay);
    },

    gridColumns() {
      const count = this.displayedSpeakers.length;
      if (count <= 1) return 1;
      if (count <= 3) return 3;
      if (count <= 4) return 4;
      if (count <= 6) return 6;
      return Math.min(Math.ceil(Math.sqrt(count)), 6);
    },

    remainingSpeakersCount() {
      return this.activeSpeakers.length - this.displayedSpeakers.length;
    },

    hasMoreSpeakers() {
      return this.remainingSpeakersCount > 0;
    },

    layoutClass() {
      const totalCount = this.activeSpeakers.length;
      if (totalCount <= 1) return 'layout-duo';
      if (totalCount <= 3) return 'layout-grid-small';
      return 'layout-main-with-grid';
    },

    gridClass() {
      const count = this.activeSpeakers.length;
      if (count <= 1) return 'grid-duo';
      if (count <= 3) return 'grid-small';
      return 'grid-large';
    },

    shouldUseMediumSize() {
      const filteredSpeakers = this.speakers.filter(s => this.mainHost ? s.id !== this.mainHost.id : true);
      return filteredSpeakers.length <= 3;
    }
  },

  methods: {
    requestToSpeak() {
      this.$emit('request-speak');
    },
  }
}
</script>

<style lang="scss" scoped>
.voice-room-speakers {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;

  .speaker {
    text-align: center;

    .speaker-avatar {
      position: relative;
      border-radius: 50%;
      border: 3px solid transparent;
      padding: 3px;
      margin: 0 auto;
      transition: border-color 0.3s ease, box-shadow 0.3s ease;

      &.active {
        border-color: #A644FB;
      }

      &.speaking {
        animation: speaking-pulse 0.6s infinite alternate;
      }

      img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }

      .mic-status {
        position: absolute;
        bottom: 0;
        right: 0;
        background: #666;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid #2A0F38;
        color: white;
        stroke-width: 2;

        &.active {
          background: #A644FB;
        }

        &:not(.active) {
          // MicOffIcon might need specific styling if the default color isn't right
          // color: #some-other-color; 
        }
      }

      .speaker-level {
        position: absolute;
        bottom: -2px;
        right: -2px;
        background: #FF9500;
        border-radius: 10px;
        padding: 2px 6px;
        font-size: 10px;
        border: 2px solid #2A0F38;
      }
    }

    .speaker-name {
      margin-top: 8px;
      font-size: 14px;
      color: white;
      opacity: 0.9;
    }

    &.show-more {
      .speaker-avatar {
        width: 60px;
        height: 60px;
        background: rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        
        span {
          color: white;
          font-size: 16px;
          font-weight: 600;
        }
      }
    }
  }

  .main-speaker {
    .speaker-avatar {
      width: 120px;
      height: 120px;

      .mic-status {
        width: 32px;
        height: 32px;
        font-size: 14px;
      }
    }

    .speaker-name {
      font-size: 16px;
      font-weight: 500;
    }
  }

  .speakers-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 800px;
    margin: 0 auto;

    .speaker {
      &.medium-size {
        .speaker-avatar {
          width: 100px;
          height: 100px;

          .mic-status {
            width: 28px;
            height: 28px;
            font-size: 12px;
          }
        }
      }

      &:not(.medium-size) .speaker-avatar {
        width: 60px;
        height: 60px;

        .mic-status {
          width: 20px;
          height: 20px;
          font-size: 10px;
        }
      }
    }
  }

  &.layout-duo {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 32px;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;

    .main-speaker {
      margin: 0;
    }

    .speakers-grid {
      display: flex;
      width: auto;
      flex-wrap: nowrap;
      margin: 0;
      justify-content: center;
    }
  }

  &.layout-grid-small {
    .speakers-grid {
      max-width: 400px;
    }
  }

  &.layout-main-with-grid {
    .speakers-grid {
      padding: 0;
    }
  }

  .request-speaker-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 60px; 
    min-height: 84px;

    &.medium-size {
      min-width: 100px;
      min-height: 124px;
    }
  }
}

.request-speak-btn {
  width: 4rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-style: dashed;
}

@keyframes speaking-pulse {
  from {
    box-shadow: 0 0 5px 1px rgba(76, 175, 80, 0.3);
  }
  to {
    box-shadow: 0 0 20px 7px rgba(76, 175, 80, 0.9);
  }
}
</style> 